// Translated intro paragraph + lazy photo strip under a section page hero (SEO copy + internal links).
import React from 'react';
import { useHotel } from '../context/HotelContext';
import { useLanguage } from '../context/LanguageContext';
import { getIntro, STRIP_CATEGORIES, STRIP_KEYWORDS, STRIP_PENALTY } from '../i18n/intros';

// Score every photo by what its caption actually says (AI-described photos are the most trustworthy),
// then fall back to room categories to fill the strip.
const scorePhoto = (g, slug) => {
  if (g.caption_source !== 'ai') return 0; // templated captions describe the room type, not the picture
  const text = ((g.caption_translations || {}).en || '').toLowerCase();
  let score = 0;
  for (const [kw, w] of STRIP_KEYWORDS[slug] || []) if (text.includes(kw)) score += w;
  if (!['rooms', 'gallery'].includes(slug)) for (const [kw, w] of STRIP_PENALTY) if (text.includes(kw)) score += w;
  if (g.caption_source === 'ai') score += 2;
  return score;
};

const pickStrip = (gallery, slug) => {
  const cats = STRIP_CATEGORIES[slug] || [];
  const out = [];
  const seen = new Set();
  // 1) caption-driven picks: best-scoring photos first, max 2 per room type so the strip stays varied
  const ranked = gallery.map((g) => ({ g, s: scorePhoto(g, slug) })).filter((x) => x.s > 0).sort((a, b) => b.s - a.s);
  const perRoom = {};
  for (const { g } of ranked) {
    if (out.length >= 5) break;
    const k = g.room_id ?? g.category;
    if ((perRoom[k] || 0) >= 2 || seen.has(g.url)) continue;
    perRoom[k] = (perRoom[k] || 0) + 1; seen.add(g.url); out.push(g);
  }
  if (out.length >= 5) return out;
  // 2) fallback: category round-robin for whatever is still missing (never a penalised photo)
  const okFallback = (g) => !seen.has(g.url) && scorePhoto(g, slug) >= 0;
  // Round-robin over the preferred categories so the strip shows variety, not 5 shots of one room.
  for (let round = 0; round < 3 && out.length < 5; round += 1) {
    for (const cat of cats) {
      const pool = gallery.filter((g) => g.category === cat && okFallback(g));
      const pick = pool[Math.min(round, pool.length - 1)];
      if (pick && !seen.has(pick.url)) { seen.add(pick.url); out.push(pick); }
      if (out.length >= 5) break;
    }
  }
  return out.slice(0, 5);
};

const SectionIntro = ({ slug }) => {
  const { lang } = useLanguage();
  const { gallery } = useHotel();
  const text = getIntro(lang, slug);
  if (!text) return null;
  const photos = pickStrip(gallery || [], slug);

  const strip = photos.map((p, i) => (
    <figure key={p.url} className={`relative overflow-hidden rounded-xl bg-gray-100 ${i === 0 ? 'col-span-2 row-span-2' : ''}`}>
      <img
        src={p.url}
        alt={(p.caption_translations || {})[lang] || (p.caption_translations || {}).en || p.alt || 'Hotel La Rosa Negra, Ulcinj'}
        loading="lazy"
        decoding="async"
        className="w-full h-full object-cover aspect-[4/3] hover:scale-105 transition-transform duration-500"
        data-testid={`section-intro-photo-${i}`}
      />
    </figure>
  ));

  return (
    <section className="bg-white py-10 sm:py-14" aria-label="Introduction" data-testid="section-intro">
      <div className="container mx-auto px-4 lg:px-8 grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-5">
          <p className="text-base md:text-lg text-gray-700 leading-relaxed" data-testid="section-intro-text">{text}</p>
        </div>
        {strip.length > 0 && (
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="section-intro-strip">{strip}</div>
        )}
      </div>
    </section>
  );
};

export default SectionIntro;
